<template>
    <div class="container py-10 mx-auto px-1 sm:px-0">

        <!-- Wrapper -->
        <div class="lg:border">
            <!-- Parameters -->
            <div class="lg:bg-neutral-200/50 rounded-tl-lg rounded-tr-lg px-8 py-4 border-b">
                <h1 class="font-bold mb-8 text-3xl">Card Price Search Tool</h1>

                <form @submit.prevent="fetchCards">
                    <!-- Search Query -->
                    <div class="flex flex-col mb-4">
                        <label for="q">Search Query</label>
                        <input
                            :class="inputStyles"
                            class="w-full py-4 px-2 text-lg"
                            v-model="filters.q"
                            type="text"
                            placeholder="Search cards"
                            name="q"
                        />
                    </div>

                    <!-- Min. Price -->
                    <div class="flex flex-col lg:flex-row lg:gap-y-0 gap-y-4 lg:gap-x-4 search-row">
                        <div class="flex flex-col">
                            <label for="price_min">Min. Price</label>
                            <input
                                :class="inputStyles"
                                class="py-2 px-2 sm:w-1/2 lg:w-3/4"
                                v-model.number="filters.price_min"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                name="price_min"
                            />
                        </div>

                        <!-- Max Price -->
                        <div class="flex flex-col">
                            <label for="price_max">Max. Price</label>
                            <input
                                :class="inputStyles"
                                class="py-2 px-2 sm:w-1/2 lg:w-3/4"
                                v-model.number="filters.price_max"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                name="price_max"
                            />
                        </div>

                        <!-- Sort By -->
                        <div class="flex flex-col min-w-40">
                            <label for="sort">Sort By</label>
                            <select
                                :class="inputStyles"
                                class="py-2 px-2 w-full"
                                v-model="filters.sort"
                                name="sort"
                            >
                                <option value="name-asc">Name (A–Z)</option>
                                <option value="name-desc">Name (Z–A)</option>
<!--                                <option value="price-asc">Price ↑</option>-->
<!--                                <option value="price-desc">Price ↓</option>-->
                            </select>
                        </div>

                        <!-- Set -->
                        <div>
                            <label>Set</label>
                            <v-select
                                :class="vue-select-match"
                                :options="sets"
                                :reduce="set => set.code"
                                v-model="filters.set"
                                :clearable="false"
                            >
                            </v-select>
                        </div>

                        <!-- Reset/Submit Buttons -->
                        <div class="flex-grow-1">
                            <div class="flex flex-row justify-end space-x-2 items-center h-full">
                                <button
                                    class="px-3 py-1 w-1/2 lg:w-24 h-16 lg:h-full border rounded bg-orange-600 text-white hover:bg-orange-700 cursor-pointer"
                                    type="button"
                                    @click="resetParameters"
                                >Reset</button>
                                <button
                                    class="px-3 py-1 w-1/2 lg:w-36 h-16 lg:h-full border rounded bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
                                    type="submit"
                                >Search</button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            <!-- Initial View -->
            <div v-if="initialView">

                <div class="w-full text-center py-12 px-6">
                    <p>Search through our database of cards to check our current prices.</p>
                </div>

            </div>

            <!-- Loading Spinner -->
            <div v-else-if="loading" class="flex justify-center flex-col items-center py-10">
                <div class="card-shuffle-spinner mb-4 relative">
                    <div class="card card-static z-0"></div>
                    <div class="card card-shuffle"></div>
                </div>
                <p>Loading cards...</p>
            </div>

            <!-- Cards List -->
            <div v-else>

                <!-- Pagination Top -->
                <div v-if="meta.total > meta.per_page" class="my-6 flex justify-start ml-6 space-x-2">
                    <button
                        :disabled="meta.current_page === 1"
                        @click="changePage(meta.current_page - 1)"
                        class="px-6 py-1 border rounded cursor-pointer hover:bg-white bg-transparent transition-colors disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:hover:bg-neutral-200 disabled:text-gray-400"
                    >
                        Prev
                    </button>
                    <button
                        :disabled="meta.current_page === meta.last_page"
                        @click="changePage(meta.current_page + 1)"
                        class="px-6 py-1 border rounded cursor-pointer hover:bg-white bg-transparent transition-colors disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:hover:bg-neutral-200 disabled:text-gray-400"
                    >
                        Next
                    </button>
                </div>

                <table v-if="cards.length > 0" class="w-full min-w-[600px] border-collapse rounded-tl-lg rounded-tr-lg">
                    <!-- Table Head-->
                    <thead class="bg-black text-white">
                    <tr class="text-left h-24">
                        <th class="py-1 px-3 text-center">Image</th>
                        <th class="py-1 px-3">Product Name</th>
                        <th class="py-1 px-3">Print</th>
                        <th class="py-1 px-3">Condition</th>
                        <th class="py-1 px-3 text-center">Market Price</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                        class="not-last:border-b border-neutral-200 hover:bg-neutral-100"
                        v-for="(card, index) in cards"
                        :key="index"
                    >
                        <!-- Image -->
                        <td>
                            <div class="p-2 w-full flex items-center justify-center">
                                <div>
                                    <img class="lg:w-44 w-24 rounded-xl shadow-2xl" :src="card.images.thumb ?? '/assets/img/card_back.jpeg'" :alt="card.name">
                                </div>
                            </div>
                        </td>

                        <!-- Product Name -->
                        <td>
                            <div class="py-4 w-full px-2">
                                <p class="text-lg font-semibold">{{ card.name }}</p>
                                <p><small>{{ card.set }}</small></p>
                            </div>
                        </td>

                        <!-- Print -->
                        <td>
                            <div class="py-4 w-full px-2">
                                <span v-if="getPrintTypes(card).length === 1">{{ formatLabel(getPrintTypes(card)[0]) }}</span>
                                <select v-else class="w-full hover:bg-white bg-transparent transition-colors lg:w-3/4 border border-neutral-200 rounded-md py-2 px-1" v-model="card.selectedPrint">
                                    <option v-for="type in getPrintTypes(card)" :key="type" :value="type">{{ formatLabel(type) }}</option>
                                </select>
                            </div>
                        </td>

                        <!-- Condition -->
                        <td>
                            <div class="py-4 w-full px-2">
                                <span v-if="getConditionTypes(card).length === 1">{{ formatLabel(getConditionTypes(card)[0]) }}</span>
                                <select v-else class="w-full lg:w-1/2 border hover:bg-white bg-transparent transition-colors  border-neutral-200 rounded-md py-2 px-1" v-model="card.selectedCondition">
                                    <option v-for="condition in getConditionTypes(card)" :key="condition" :value="condition">{{ formatLabel(condition) }}</option>
                                </select>
                            </div>
                        </td>

                        <!-- Card Price -->
                        <td>
                            <div class="py-4 w-full px-2 text-center">

                                <div
                                    v-if="getSelectedPrice(card) !== null"
                                    class="relative text-center flex space-x-2 items-center">


                                    <div class="mx-auto">
                                        <span
                                            class="w-full text-lg font-bold"
                                            :class="(getSelectedPriceStaleness(card)) ? 'line-through' : ''"
                                        >
                                        ${{ getSelectedPrice(card) }}
                                    </span>

                                        <InfoPricePopup v-if="getSelectedPriceStaleness(card)" />
                                    </div>

                                </div>
                                <span v-else class="text-gray-500 italic w-full text-lg font-bold">N/A</span>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>

                <div v-else>
                    <div class="w-full text-center py-12 px-6">
                        <p>No cards found. Try another search!</p>
                    </div>
                </div>
            </div>

            <!-- Pagination Bottom -->
            <div v-if="meta.total > meta.per_page" class="my-6 flex justify-center space-x-2">
                <button
                    :disabled="meta.current_page === 1"
                    @click="changePage(meta.current_page - 1)"
                    class="px-10 py-1 border rounded cursor-pointer hover:bg-white bg-transparent transition-colors disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:hover:bg-neutral-200 disabled:text-gray-400"
                >
                    Prev
                </button>
                <button
                    :disabled="meta.current_page === meta.last_page"
                    @click="changePage(meta.current_page + 1)"
                    class="px-10 py-1 border rounded cursor-pointer hover:bg-white bg-transparent transition-colors disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:hover:bg-neutral-200 disabled:text-gray-400"
                >
                    Next
                </button>
            </div>
        </div>

    </div>
</template>

<style>
    /* Global Search Row */
    .search-row > * input:not(.vs__search),
    .search-row > * select,
    .search-row > * .vs__dropdown-toggle {
        height: 3rem;
        min-height: 3rem;
        box-sizing: border-box;
    }
    /* Override vue-select styles */
    .vs__dropdown-toggle {
        border-radius: 0.375rem;
        border: 1px solid black;
        background-color: white;
        color: black;
        padding: 0.5rem;
        min-height: unset;
        box-shadow: none;
        caret-color: black;

        width: auto;
        min-width: 18rem;
        max-width: 20rem;
        box-sizing: border-box;
        white-space: nowrap;

        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    /* Selected Options Section */
    .vs__selected-options {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    /* Arrow for Select */
    .vs__actions {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
    }

    /* Focus outline mimic */
    .vs__dropdown-toggle:focus,
    .vs__dropdown-toggle:focus-within {
        outline: none;
        border-width: 1px;
        border-color: black;
    }

    /* Input field on Select */
    .vs__search {
        position: absolute;
        clip: rect(0 0 0 0);
        pointer-events: none;
    }

    /* When Select drawer is open, modify the search input to be visible */
    .vs--open .vs__search {
        position: static;
        clip: auto;
        pointer-events: auto;
        width: 100%;
    }

    /* Selected option */
    .vs__selected {
        color: black;
        height: fit-content;
    }

    /* Remove default arrow styling so it’s consistent */
    .vs__open-indicator {
        fill: black;
    }

    /* Dropdown menu */
    .vs__dropdown-menu {
        border-radius: 0.375rem;
        border: 1px solid black;
        background-color: white;
    }

    /* Options */
    .vs__dropdown-menu {
        width: 100%;
    }

    .vs__dropdown-option {
        color: black;
        padding: 0.5rem;
    }
    .vs__dropdown-option--highlight {
        background-color: #f3f4f6;
    }

    .vs__clear {
        display: none;
    }

    /* Card Shuffle loading animation */
    .card-shuffle-spinner {
        position: relative;
        width: 60px;
        height: 60px;
        perspective: 600px;
    }

    .card-shuffle-spinner .card {
        position: absolute;
        width: 40px;
        height: 60px;
        border-radius: 6px;
        top: 0;
        left: 10px;
        transform-origin: center bottom;
        background: color-mix(in oklab, var(--color-neutral-200) 50%, transparent);
        background-size: cover;
    }

    .card-shuffle {
        animation: shuffle 0.75s infinite ease-in-out;
    }

    @keyframes shuffle {
        0% {
            transform: translateX(0) rotate(0deg) scale(1);
            z-index: 1;
        }
        25% {
            transform: translateX(15px) translateY(-15px) rotate(15deg) scale(1.25);
        }
        50% {
            transform: translateX(45px) translateY(0) rotate(0deg) scale(1);
            z-index: -1;
        }
        75% {
            transform: translateX(0) translateY(0) rotate(0deg) scale(1);
        }
        100% {
            transform: translateX(0) rotate(0deg) scale(1);
        }
    }

</style>
<script setup>
    import 'vue-select/dist/vue-select.css';
    import { reactive, ref, onMounted } from 'vue';
    import InfoPricePopup from "../components/InfoPricePopup.vue";

    const filters = reactive({
        q: '',
        price_min: null,
        price_max: null,
        set: "",
        sort: 'name-asc',
        page: 1
    });
    const cards = ref([]);
    const sets = ref([]);
    const loading = ref(false);
    const initialView = ref(true);
    const meta = reactive({
       current_page: 1,
       last_page: 1,
       per_page: 50,
       total: 0
    });

    const inputStyles = 'rounded-md border bg-white text-black border-black focus:outline-none';

    /* Custom Orderings */
    const printOrder = {
        normal: 0,
        foil: 1
    }

    const conditionOrder = {
        near_mint: 0,
        lightly_played: 1,
        moderately_played: 2,
        damaged: 3
    }

    function buildQuery() {
        const query = {}
        for (const key in filters) {
            if (filters[key] !== null && filters[key] !== '') {
                query[key] = filters[key]
            }
        }
        return query
    }

    async function fetchSets() {
        try {
            const res = await axios.get('/api/v1/sets')
            sets.value = [
                { label: '---', code: null }, // Default empty option
                ...res.data.data.map(set => ({
                    label: `${set.clean_name} (${set.abbrev})`,
                    code: set.slug,
                }))
            ];

            filters.set = sets.value[0].code;
        } catch (e) {
            console.error('Fetch failed:', e)
        }
    }

    async function fetchCards() {
        initialView.value = false;
        loading.value = true

        try {
            // Uncomment this to require a set for the query
            // if (!filters.set) {
            //     return;
            // }

            const res = await axios.get('/api/v1/search', { params: buildQuery() })

            cards.value = res.data.data.map(card => {
                // Default to normal if applicable
                const printTypes = Object.keys(card.prices || {})
                const defaultPrint = printTypes.includes('normal') ? 'normal' : printTypes[0] || null

                // Default to near_mint if applicable
                let defaultCondition = null;
                if (defaultPrint && card.prices[defaultPrint]) {
                    const conditions = Object.keys(card.prices[defaultPrint]);
                    defaultCondition = conditions.includes('near_mint') ? 'near_mint' : conditions[0] || null;
                }

                return {
                    ...card,
                    selectedPrint: defaultPrint,
                    selectedCondition: defaultCondition
                }
            });

            Object.assign(meta, res.data.meta)
        } catch (e) {
            console.error('Fetch failed:', e)
        } finally {
            loading.value = false
        }
    }

    function getPrintTypes(card) {
        const types = Object.keys(card.prices || [])

        return types.sort((a, b) => {
            const aRank = printOrder[a] ?? 999
            const bRank = printOrder[b] ?? 999
            return aRank - bRank
        })
    }

    function getConditionTypes(card) {
        const allConditions = Object.values(card.prices).flatMap(obj => Object.keys(obj));

        return Array.from(new Set(allConditions.sort((a, b) => {
            const aRank = conditionOrder[a] ?? 999
            const bRank = conditionOrder[b] ?? 999
            return aRank - bRank
        })));
    }

    function getSelectedPrice(card) {
        const { selectedPrint, selectedCondition, prices } = card
        return prices?.[selectedPrint]?.[selectedCondition]?.price ?? null
    }

    function getSelectedPriceStaleness(card) {
        const { selectedPrint, selectedCondition, prices } = card
        return prices?.[selectedPrint]?.[selectedCondition]?.stale ?? true
    }

    function formatLabel(str) {
        return str
            .replace(/_/g, ' ') // snake_case to space
            .replace(/\b\w/g, l => l.toUpperCase()) // capitalize each word
    }

    function changePage(page) {
        filters.page = page;
        fetchCards();
    }

    function resetParameters() {
        filters.q = '';
        filters.price_min = null;
        filters.price_max = null;
        filters.set = sets.value[0].code;
        filters.sort = 'name-asc';
    }

    onMounted(() => {
        fetchSets();
    });
</script>
